import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/Button";

/**
 * §resilience — top-level React error boundary. React has no functional-component
 * equivalent for getDerivedStateFromError/componentDidCatch, so this stays a
 * class component. Wraps the whole route tree (App.tsx) so a render error in any
 * page shows a recoverable "something went wrong" card (matching the NoAccess
 * glass conventions) instead of unmounting the app to a blank white screen.
 *
 * No Sentry hook: there is no frontend Sentry SDK in web/ (Sentry is backend-only),
 * so we log to console.error — still visible in devtools / any console capture.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError(): { hasError: true } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("Unhandled UI error:", error, info.componentStack);
  }

  private handleReload = () => {
    // Full reload is the safest recovery: it re-runs main.tsx (theme, tenant
    // attr, providers) from a clean state. The persisted react-query cache +
    // localStorage session survive, so the operator lands back where they were
    // without re-login.
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        role="alert"
        className="flex min-h-screen items-center justify-center px-6"
        style={{ background: "var(--canvas-base, #0c0e18)" }}
      >
        <div
          className="w-full max-w-md rounded-2xl border p-8 text-center"
          style={{
            background: "var(--glass-1)",
            borderColor: "var(--glass-hairline-strong)",
            boxShadow: "var(--shadow-rim), 0 30px 80px -20px rgba(0,0,0,0.6)",
            backdropFilter: "blur(28px) saturate(180%)",
          }}
        >
          <h1
            className="text-xl font-semibold"
            style={{
              fontFamily: "var(--font-display)",
              color: "var(--fg-1)",
              letterSpacing: "-0.015em",
            }}
          >
            Something went wrong
          </h1>
          <p className="mt-2 text-sm" style={{ color: "var(--fg-3)", lineHeight: 1.55 }}>
            An unexpected error interrupted this page. Reloading usually fixes it —
            your session stays signed in.
          </p>
          <div className="mt-6">
            <Button variant="primary" onClick={this.handleReload} className="w-full">
              Reload
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
